import { useEffect, useState, useCallback } from 'react';
import type {
  UsersResponse,
  UserRecord,
  TestEmailResponse,
  StationContact,
  StationContactsResponse,
} from '@shared/api-responses';
import type { UserRole } from '@shared/types';
import { ROLES } from '@shared/roles';
import { apiFetch } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';

const ROLE_OPTIONS: UserRole[] = ['crew', 'logistics', 'admin'];

// ── Email integration test ───────────────────────────────────────────
// Lets an admin fire a real message through Resend to any address, so a
// non-developer can confirm delivery (or capture the exact provider error)
// when troubleshooting "an email never arrived".
function EmailTestCard() {
  const { user } = useAuth();
  const [to, setTo] = useState(user?.email ?? '');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<TestEmailResponse | null>(null);

  async function send() {
    setSending(true);
    setResult(null);
    try {
      const r = await apiFetch<TestEmailResponse>('/admin/test-email', { method: 'POST', body: { to } });
      setResult(r);
    } catch (e) {
      // Only reached on a non-2xx (e.g. invalid address 400); the send itself
      // returns 200 with sent=false so the provider error is preserved above.
      setResult({ sent: false, to, from: '', error: e instanceof Error ? e.message : String(e) });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="glass rounded-xl p-4 mb-6">
      <h2 className="text-sm font-semibold text-zinc-300 mb-1">Email integration test</h2>
      <p className="text-xs text-zinc-500 mb-3">
        Send a test message through Resend to confirm delivery. Use your own address, or the one that isn&apos;t
        receiving mail.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="name@dcvfd.org"
          className="flex-1 rounded-lg bg-surface-raised border border-border-subtle px-3 py-2 text-sm text-white placeholder-zinc-500"
        />
        <button
          onClick={send}
          disabled={sending || !to.trim()}
          className="rounded-lg bg-dcvfd-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 whitespace-nowrap"
        >
          {sending ? 'Sending…' : 'Send test'}
        </button>
      </div>
      {result &&
        (result.sent ? (
          <p className="mt-3 text-sm text-ems-green">
            ✓ Sent to {result.to} from {result.from}. Check that inbox (and spam).
          </p>
        ) : (
          <p className="mt-3 text-sm text-ems-red break-words">✗ Failed: {result.error ?? 'unknown error'}</p>
        ))}
    </div>
  );
}

// ── Station officer emails ───────────────────────────────────────────
// Each station's officer distro hears when a request from the station is sent
// in and when an order for it is delivered (kitchen and cleaning). Blank turns
// a station's emails off.
function StationEmailRow({ station, onSaved }: { station: StationContact; onSaved: (s: StationContact[]) => void }) {
  const [value, setValue] = useState(station.officer_email ?? '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const dirty = value.trim().toLowerCase() !== (station.officer_email ?? '');

  async function save() {
    setState('saving');
    setError(null);
    try {
      // Direct fetch so the worker's reason (wrong domain, bad address) shows as-is.
      const res = await fetch(`/api/admin/stations/${station.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ officerEmail: value.trim() || null }),
      });
      const data = (await res.json().catch(() => null)) as (StationContactsResponse & { error?: string }) | null;
      if (!res.ok || !data?.stations) {
        setError(data?.error ?? 'Could not save.');
        setState('error');
        return;
      }
      onSaved(data.stations);
      setState('saved');
    } catch {
      setError('Could not save.');
      setState('error');
    }
  }

  return (
    <div className="glass rounded-lg px-4 py-3">
      <div>
        <div className="text-sm text-white font-medium">{station.name}</div>
        <div className="mt-1.5 flex gap-2">
          <input
            type="email"
            inputMode="email"
            autoCapitalize="none"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setState('idle');
            }}
            placeholder="No emails (blank)"
            aria-label={`Officer email for ${station.name}`}
            className="flex-1 min-w-0 rounded-lg bg-surface-raised border border-border-subtle px-3 py-2 text-sm text-white placeholder-zinc-500"
          />
          <button
            onClick={save}
            disabled={!dirty || state === 'saving'}
            className="rounded-lg bg-dcvfd-accent px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            {state === 'saving' ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {state === 'saved' && <p className="mt-1.5 text-xs text-ems-green">Saved.</p>}
      {state === 'error' && <p className="mt-1.5 text-xs text-ems-red">{error}</p>}
    </div>
  );
}

export function StationEmailsCard() {
  const [stations, setStations] = useState<StationContact[] | null>(null);

  useEffect(() => {
    apiFetch<StationContactsResponse>('/admin/stations')
      .then((r) => setStations(r.stations))
      .catch(() => setStations([]));
  }, []);

  return (
    <div className="mb-6">
      <h2 className="text-sm font-semibold text-zinc-300 mb-1">Station officer emails</h2>
      <p className="text-xs text-zinc-500 mb-3">
        Emailed the item list when a request from the station is sent in, and again when its order is delivered. Kitchen
        and cleaning both. Leave blank to turn a station&apos;s emails off.
      </p>
      {stations === null ? (
        <p className="text-zinc-400 text-sm">Loading…</p>
      ) : (
        <div className="space-y-2">
          {stations.map((s) => (
            <StationEmailRow key={s.id} station={s} onSaved={setStations} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch<UsersResponse>('/users')
      .then((r) => setUsers(r.users))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  async function changeRole(id: number, role: UserRole) {
    await apiFetch(`/users/${id}/role`, { method: 'PUT', body: { role } });
    load();
  }

  async function toggleActive(u: UserRecord) {
    await apiFetch(`/users/${u.id}/active`, { method: 'PUT', body: { is_active: !u.is_active } });
    load();
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="text-xl font-bold text-white mb-4">Admin</h1>

      <StationEmailsCard />
      <EmailTestCard />

      <h2 className="text-sm font-semibold text-zinc-300 mb-2">Users ({users.length})</h2>
      {loading ? (
        <p className="text-zinc-400">Loading…</p>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.id} className="glass rounded-lg px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white font-medium truncate">{u.name}</div>
                <div className="text-xs text-zinc-400 truncate">{u.email ?? u.auth_method ?? '—'}</div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={u.role}
                  onChange={(e) => changeRole(u.id, e.target.value as UserRole)}
                  className="rounded-lg bg-surface-raised border border-border-subtle px-2 py-1.5 text-xs text-white"
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {ROLES[r].label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => toggleActive(u)}
                  className={`rounded-lg px-2 py-1.5 text-xs font-medium ${
                    u.is_active ? 'bg-ems-green/20 text-ems-green' : 'bg-ems-red/20 text-ems-red'
                  }`}
                >
                  {u.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
